<template>
  <div>
    <LayoutHeader />

    <main>
      <!-- Hero вне client-only, чтобы LCP-изображение было в исходном HTML -->
      <section class="hero-section">
        <HeroSection />
      </section>
      <section class="hero-features-section">
        <FeatureSection />
      </section>
      <section id="courses-section"
               class="categories-section">
        <CategoriesSection />
      </section>
      <section class="certificate-section">
        <CertificateSection />
      </section>
      <section id="popular-courses-section"
               class="popular-courses-section">
        <PopularCoursesSection :locale="effectiveLocale" />
      </section>
      <ExpertsSection />
      <!-- <EventsSection /> -->
      <section class="about-us-section">
        <AboutUsSection />
      </section>
      <section class="faq-section">
        <FaqSection />
      </section>
      <section id="application-form-section" class="application-form-section">
        <ApplicationFormSection />
      </section>
      <section id="articles-section" class="home-articles-section">
        <ArticlesSection :locale="effectiveLocale" />
      </section>
      <section class="partners-section">
        <PartnersSection />
      </section>
    </main>

    <LayoutFooter />
    <LayoutBackToTop />
  </div>
</template>

<script setup lang="ts">
import { useSeo } from '@/composables/seo'
import { useLocaleSwitch } from '@/composables/locale'
import { SUPPORTED_LOCALES } from '@/domain/services/i18n.service'
import { useArticleStore } from '@/stores/article'
import { useEventStore } from '@/stores/event'

const route = useRoute()
const { locale, t } = useI18n()
const { getCurrentLocale } = useLocaleSwitch()

const routeLocale = route.params.locale as string
if (!SUPPORTED_LOCALES.includes(routeLocale as any)) {
  throw createError({ statusCode: 404, fatal: true })
}

const articleStore = useArticleStore()
const eventStore = useEventStore()

// Определяем эффективную локаль для компонентов (из роута или i18n)
const effectiveLocale = computed(() => getCurrentLocale() || locale.value)
const localeHasArticles = computed(() => articleStore.localesWithArticles[effectiveLocale.value] === true)
const localeHasEvents = computed(() => eventStore.localesWithEvents[effectiveLocale.value] === true)

watch(
  effectiveLocale,
  (loc) => {
    if (!loc) return
    articleStore.checkLocaleHasArticles(loc)
    eventStore.checkLocaleHasEvents(loc)
  },
  { immediate: true },
)

watch([localeHasArticles, localeHasEvents, effectiveLocale], ([hasArticles, hasEvents, loc]) => {
  if (!hasArticles && !hasEvents) {
    console.info(`[index] Blocks hidden for locale "${loc}": articles and events`)
  }
})

// Настройка SEO для главной страницы (title/description из словаря по выбранной локали)
const { setupSeo, createEducationalOrganizationSchema } = useSeo()

const indexSeoTitle = computed(() => t('index.seo.title'))
const indexSeoDescription = computed(() => t('index.seo.description'))

watchEffect(() => {
  setupSeo({
    title: indexSeoTitle.value,
    description: indexSeoDescription.value,
    schema: createEducationalOrganizationSchema(),
    locale: effectiveLocale.value as 'en' | 'uk' | 'ru'
  })
})

// Preload LCP image (first hero image) for faster Largest Contentful Paint
const heroPreloadLink = computed(() =>
  [{
    rel: 'preload',
    href: '/assets/images/index/hero/hero-bg-1.webp',
    as: 'image' as const,
    type: 'image/webp',
    fetchpriority: 'high' as const
  }]
)
useHead({ link: heroPreloadLink })

function scrollToSection(sectionId: string) {
  nextTick(() => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

onMounted(() => {
  if (route.hash?.startsWith('#')) {
    scrollToSection(route.hash.slice(1))
    history.replaceState(null, '', route.path)
  }
})

watch(() => route.hash, (hash) => {
  if (hash?.startsWith('#')) {
    scrollToSection(hash.slice(1))
  }
})
</script>

<style scoped>
main {
  margin: 0 auto;
  overflow-x: hidden;
  width: 100%;
}

section {
  margin: 0 auto;
  overflow-x: hidden;
  width: 100%;
}

.hero-section {
  margin: 0 auto;
  overflow: hidden;
  background: linear-gradient(90deg, #D2EBF0 0%, #FCF3E0 100%);
}

.hero-features-section {
  margin-top: -100px;
  padding: 0 20px 0;
}

@media (max-width: 991px) {
  .hero-features-section {
    margin-top: -96px;
  }
}
@media (max-width: 720px) {
  .hero-features-section {
    margin-top: -126px;
  }
}
@media (max-width: 480px) {
  .hero-features-section {
    margin-top: -76px;
    padding: 0 15px 0;
  }
}
@media (max-width: 400px) {
  .hero-features-section {
    margin-top: -100px;
  }
}
.about-us-section {
  background: #fff;
}

.partners-section {
  background: #fff;
  margin-top: 60px;
}

.application-form-section {
  background: #fff;
}

.home-articles-section {
  background: #fff;
  margin: 0 auto;
  overflow-x: hidden;
  width: 100%;
}

.faq-section {
  background: var(--color-bg-faq);
}
</style>
